// ============================================================
// PERRY — FOE WEAPONS
// What the road is holding.
//
// Same discipline as TreasureBody and PerryBody: built out of
// primitives, painted with cached canvas textures, zero external
// assets, zero new lights. A weapon is a GROUP with its origin at
// the grip, +Y up the haft, so a hand can hold any of them with
// the same transform and the body never has to special-case one.
//
// THE EXCEPTION LAW (caP'n, 2026-07-27) applies here more than
// anywhere: this file is a switch statement over a string. Adding
// a gun, a coin, a Terror card, a comet or a dolphin with a laser
// on it is ONE case in `FoeWeapon` and one entry in an enemy's
// `arms` list. Nothing else in the codebase learns about it.
//
//   "scythe"  a harvest blade the fire kept
//   "pale"    a hooked fence-pale, iron-banded, splintered
//   "kunai"   Th'rakk's hooked blade — the head of the Cinder Haul
//   "none"    hands. Which is also a weapon, and always was.
// ============================================================

import { useMemo } from "react";
import * as THREE from "three";
import { mulberry32 } from "./qteEngine";

// ── PITTED IRON — hammered, rusted, one bright honed streak ───
let IRON_TEX = null;
export function ironTexture() {
  if (IRON_TEX) return IRON_TEX;
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const g = c.getContext("2d");
  const R = mulberry32(31771);

  // base: cold grey-brown metal with a vertical hammer grain
  const base = g.createLinearGradient(0, 0, 256, 0);
  base.addColorStop(0.00, "#33302e");
  base.addColorStop(0.34, "#6a6a6c");
  base.addColorStop(0.52, "#8e9096");
  base.addColorStop(0.70, "#5a5854");
  base.addColorStop(1.00, "#2a2624");
  g.fillStyle = base; g.fillRect(0, 0, 256, 256);

  // hammer facets
  for (let i = 0; i < 130; i++) {
    g.globalAlpha = 0.05 + R() * 0.12;
    g.fillStyle = R() > 0.5 ? "#a9adb4" : "#211d1c";
    g.beginPath();
    g.ellipse(R() * 256, R() * 256, 6 + R() * 20, 3 + R() * 8, R() * 3.14, 0, Math.PI * 2);
    g.fill();
  }
  // rust blooms and soot
  for (let i = 0; i < 70; i++) {
    g.globalAlpha = 0.06 + R() * 0.16;
    g.fillStyle = R() > 0.55 ? "#6e3418" : "#191412";
    g.beginPath();
    g.arc(R() * 256, R() * 256, 3 + R() * 14, 0, Math.PI * 2);
    g.fill();
  }
  // the honed edge — a hot bright rule down one side
  g.globalAlpha = 1;
  const edge = g.createLinearGradient(0, 0, 0, 34);
  edge.addColorStop(0, "#f2f4f8");
  edge.addColorStop(1, "rgba(150,150,158,0)");
  g.fillStyle = edge; g.fillRect(0, 0, 256, 34);

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  IRON_TEX = t;
  return t;
}

// ── CHARRED HAFT — wood that has been in a fire and lost ──────
let HAFT_TEX = null;
export function haftTexture() {
  if (HAFT_TEX) return HAFT_TEX;
  const c = document.createElement("canvas");
  c.width = 128; c.height = 256;
  const g = c.getContext("2d");
  const R = mulberry32(9931);
  g.fillStyle = "#1b1512"; g.fillRect(0, 0, 128, 256);
  for (let i = 0; i < 46; i++) {
    g.globalAlpha = 0.2 + R() * 0.4;
    g.strokeStyle = R() > 0.72 ? "#6b4a2c" : "#0c0908";
    g.lineWidth = 1 + R() * 3.4;
    const x = R() * 128;
    g.beginPath();
    g.moveTo(x, 0);
    g.bezierCurveTo(x + (R() - 0.5) * 16, 90, x + (R() - 0.5) * 16, 170, x + (R() - 0.5) * 12, 256);
    g.stroke();
  }
  // ember still alive in the grain
  for (let i = 0; i < 26; i++) {
    g.globalAlpha = 0.35 + R() * 0.5;
    g.fillStyle = R() > 0.5 ? "#c8571f" : "#7a2c10";
    g.beginPath();
    g.arc(R() * 128, R() * 256, 0.8 + R() * 2.2, 0, Math.PI * 2);
    g.fill();
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  HAFT_TEX = t;
  return t;
}

// ── Shared materials. One instance each, reused by every weapon
//    on the road, so a hundred husks cost one shader compile. ──
let IRON_MAT = null;
export function ironMaterial() {
  if (!IRON_MAT) {
    IRON_MAT = new THREE.MeshStandardMaterial({
      map: ironTexture(), metalness: 0.82, roughness: 0.38,
      emissive: new THREE.Color("#2a1108"), emissiveIntensity: 0.35,
    });
  }
  return IRON_MAT;
}
let HAFT_MAT = null;
export function haftMaterial() {
  if (!HAFT_MAT) {
    HAFT_MAT = new THREE.MeshStandardMaterial({
      map: haftTexture(), metalness: 0.05, roughness: 0.95,
      emissive: new THREE.Color("#5a1c06"), emissiveIntensity: 0.28,
    });
  }
  return HAFT_MAT;
}

// A weapon's hot rule — the line of fire still living in the
// steel. Basic + toneMapped false: it glows and costs no light.
function HotEdge({ args, position, rotation, color, opacity = 0.55 }) {
  return (
    <mesh position={position} rotation={rotation}>
      <torusGeometry args={args} />
      <meshBasicMaterial color={color} transparent opacity={opacity} toneMapped={false} depthWrite={false} />
    </mesh>
  );
}

// ════════════════════════════════════════════════════════════
// THE SCYTHE — what a farmhand had in their hands at midnight
// ════════════════════════════════════════════════════════════
// The crescent is a torus ARC squashed flat on one axis. That one
// trick is why no blade in this file needs a box or a custom
// BufferGeometry: a partial torus is already a curved blade, and
// scaling it thin on Z gives it a cross-section.
function Scythe({ hot = "#ff8a3c" }) {
  const iron = ironMaterial();
  const haft = haftMaterial();
  return (
    <group>
      {/* the snath — long, slightly bent, charred */}
      <mesh castShadow position={[0, 0.30, 0]} material={haft}>
        <cylinderGeometry args={[0.019, 0.026, 0.66, 7]} />
      </mesh>
      <mesh castShadow position={[0.035, 0.70, 0]} rotation={[0, 0, -0.16]} material={haft}>
        <cylinderGeometry args={[0.014, 0.019, 0.26, 7]} />
      </mesh>
      {/* the grip nib halfway down — the detail that says "tool" */}
      <mesh position={[0.055, 0.36, 0]} rotation={[0, 0, 1.35]} material={haft}>
        <cylinderGeometry args={[0.014, 0.016, 0.10, 6]} />
      </mesh>
      {/* iron collar where blade meets snath */}
      <mesh position={[0.06, 0.80, 0]} rotation={[Math.PI / 2, 0, 0]} material={iron}>
        <torusGeometry args={[0.028, 0.010, 6, 10]} />
      </mesh>
      {/* THE CRESCENT — a torus arc, flattened into a blade */}
      <group position={[0.06, 0.81, 0]} rotation={[Math.PI / 2, 0, 0.35]} scale={[1, 1, 0.34]}>
        <mesh castShadow material={iron}>
          <torusGeometry args={[0.30, 0.026, 6, 22, Math.PI * 0.86]} />
        </mesh>
        <HotEdge
          args={[0.322, 0.006, 4, 20, Math.PI * 0.84]}
          position={[0, 0, 0]} rotation={[0, 0, 0]} color={hot} opacity={0.6}
        />
      </group>
      {/* the point — a cone finishing the arc, so it tapers */}
      <mesh castShadow position={[0.36, 1.05, 0]} rotation={[0, 0, -1.05]} scale={[1, 1, 0.4]} material={iron}>
        <coneGeometry args={[0.028, 0.16, 6]} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE PALE — a hooked fence-pale, pulled out of a fence that
// stopped mattering. Splintered top, iron bands, a hauling hook.
// ════════════════════════════════════════════════════════════
function Pale({ hot = "#ff8a3c" }) {
  const iron = ironMaterial();
  const haft = haftMaterial();
  return (
    <group>
      <mesh castShadow position={[0, 0.42, 0]} material={haft}>
        <cylinderGeometry args={[0.026, 0.034, 0.92, 6]} />
      </mesh>
      {/* three iron bands, unevenly spaced — nothing here is neat */}
      {[0.14, 0.46, 0.74].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]} material={iron}>
          <torusGeometry args={[0.031 + i * 0.001, 0.008, 5, 10]} />
        </mesh>
      ))}
      {/* the splintered head — three cones at broken angles */}
      {[[-0.02, 0.94, 0.01, -0.22], [0.015, 0.98, -0.01, 0.14], [0.0, 0.92, -0.025, 0.05]].map((v, i) => (
        <mesh key={`s${i}`} castShadow position={[v[0], v[1], v[2]]} rotation={[0, 0, v[3]]} material={haft}>
          <coneGeometry args={[0.020 - i * 0.004, 0.16 + i * 0.05, 5]} />
        </mesh>
      ))}
      {/* THE HOOK — a torus arc riveted to the side */}
      <group position={[0.05, 0.72, 0]} rotation={[Math.PI / 2, 0, -0.5]} scale={[1, 1, 0.5]}>
        <mesh castShadow material={iron}>
          <torusGeometry args={[0.10, 0.014, 6, 14, Math.PI * 1.25]} />
        </mesh>
        <HotEdge args={[0.112, 0.005, 4, 12, Math.PI * 1.2]} color={hot} opacity={0.45} />
      </group>
      <mesh castShadow position={[0.155, 0.66, 0]} rotation={[0, 0, 1.9]} material={iron}>
        <coneGeometry args={[0.014, 0.07, 5]} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE KUNAI — the head of the Cinder Haul
// ════════════════════════════════════════════════════════════
// Th'rakk's blade. A leaf point with a hauling BARB on the spine
// — this thing's original job was dragging burning beams off a
// road, not killing bebs, and the silhouette has to say so. The
// ring pommel is where the chain lives, so the ring is modelled
// even when the chain isn't drawn.
//
// Exported by name because the STAGE also draws it, in flight,
// detached from any hand. Same geometry in both places, always.
export function Kunai({ hot = "#6fe6d4", scale = 1 }) {
  const iron = ironMaterial();
  return (
    <group scale={scale}>
      {/* wrapped grip */}
      <mesh castShadow position={[0, 0.045, 0]} material={iron}>
        <cylinderGeometry args={[0.019, 0.021, 0.13, 7]} />
      </mesh>
      {[0.0, 0.03, 0.06, 0.09].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]} material={iron}>
          <torusGeometry args={[0.021, 0.005, 4, 9]} />
        </mesh>
      ))}
      {/* the ring pommel — where the Haul is made fast */}
      <mesh castShadow position={[0, -0.055, 0]} rotation={[Math.PI / 2, 0, 0]} material={iron}>
        <torusGeometry args={[0.038, 0.010, 6, 14]} />
      </mesh>
      {/* THE LEAF — two cones base to base, flattened. A diamond
          from the side, a blade from the edge. */}
      <group position={[0, 0.20, 0]} scale={[1, 1, 0.3]}>
        <mesh castShadow position={[0, -0.045, 0]} rotation={[Math.PI, 0, 0]} material={iron}>
          <coneGeometry args={[0.046, 0.10, 7]} />
        </mesh>
        <mesh castShadow position={[0, 0.075, 0]} material={iron}>
          <coneGeometry args={[0.046, 0.16, 7]} />
        </mesh>
      </group>
      {/* the hot rule down the edge — the thing you see coming */}
      <mesh position={[0, 0.21, 0]} scale={[1, 1, 0.06]}>
        <sphereGeometry args={[0.055, 8, 10]} />
        <meshBasicMaterial color={hot} transparent opacity={0.42} toneMapped={false} depthWrite={false} />
      </mesh>
      {/* THE BARB — the hauling hook on the spine. This is the
          detail that makes it Th'rakk's and not a ninja's. */}
      <group position={[0.03, 0.13, 0]} rotation={[Math.PI / 2, 0, -0.9]} scale={[1, 1, 0.45]}>
        <mesh castShadow material={iron}>
          <torusGeometry args={[0.052, 0.010, 5, 12, Math.PI * 1.05]} />
        </mesh>
      </group>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE CHAIN — the house bead-chain, as iron
// ════════════════════════════════════════════════════════════
// A pool of link meshes with NO animation of its own. The parent
// owns the curve and writes `refs.current[i].position` in its own
// useFrame — one loop per system, never one per link. Alternate
// links are pre-rotated a quarter turn, which is the entire
// difference between "a chain" and "a row of doughnuts".
//
// The parent is also expected to set `.visible` — a chain that is
// not deployed should not be in the frame at all.
export function ChainLinks({ refs, count = 20, tint = "#8fa0a6", scale = 1 }) {
  const iron = ironMaterial();
  const geo = useMemo(
    () => new THREE.TorusGeometry(0.030 * scale, 0.0105 * scale, 5, 9),
    [scale]
  );
  const glowMat = useMemo(
    () => new THREE.MeshBasicMaterial({
      color: tint, transparent: true, opacity: 0.16,
      toneMapped: false, depthWrite: false,
    }),
    [tint]
  );
  return (
    <group>
      {Array.from({ length: count }, (_, i) => (
        <group key={i} ref={(el) => { refs.current[i] = el; }} visible={false}>
          <mesh geometry={geo} material={iron} rotation={[0, i % 2 ? Math.PI / 2 : 0, 0]} />
          <mesh geometry={geo} material={glowMat} scale={1.5} rotation={[0, i % 2 ? Math.PI / 2 : 0, 0]} />
        </group>
      ))}
    </group>
  );
}


// ════════════════════════════════════════════════════════════
// THE BILL OF BEATEN IRON — the Disciple's, and eventually yours
// ════════════════════════════════════════════════════════════
// A billhook: a long haft, a heavy forward-curving blade, and a
// back-spike, which is the part that makes a bill a bill and not
// a glaive. Old Maren named it on Perry's first night in
// Hollowmere before this model existed — "a thing with a bill of
// beaten iron" — and the Treasure of the same name has been in
// Whyllie's armoury since the founding slice with a lore line
// that quietly admits where it came from.
//
// BEATEN, not cast. The blade is FOUR overlapping torus arcs at
// slightly different radii and thicknesses rather than one clean
// crescent, which is what hammer-work looks like from six feet
// away: a curve that is nearly a curve. That is the whole trick
// here and it costs four meshes.
//
// It does not burn. Everything else on both roads is on fire or
// was; the Disciple walked into the fen on its own feet. So the
// hot rule down the edge is not fire — it is the violet the thing
// itself carries, and it is DIM, and it is the only light on it.
function Bill({ hot = "#c084f0" }) {
  const iron = ironMaterial();
  const haft = haftMaterial();
  return (
    <group>
      {/* the haft — long, straight, bound at the throat */}
      <mesh castShadow position={[0, 0.34, 0]} material={haft}>
        <cylinderGeometry args={[0.021, 0.028, 0.74, 8]} />
      </mesh>
      {[0.60, 0.645, 0.69].map((y, i) => (
        <mesh key={i} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]} material={iron}>
          <torusGeometry args={[0.026, 0.006, 4, 10]} />
        </mesh>
      ))}
      {/* the socket the head is beaten onto */}
      <mesh castShadow position={[0, 0.74, 0]} material={iron}>
        <cylinderGeometry args={[0.034, 0.028, 0.12, 8]} />
      </mesh>

      {/* ── THE HOOK ── four arcs, none of them agreeing ── */}
      <group position={[0, 0.80, 0]} scale={[1, 1, 0.26]}>
        <mesh castShadow rotation={[Math.PI / 2, 0, -0.30]} material={iron}>
          <torusGeometry args={[0.150, 0.030, 6, 16, Math.PI * 0.92]} />
        </mesh>
        <mesh castShadow rotation={[Math.PI / 2, 0, -0.22]} material={iron}>
          <torusGeometry args={[0.164, 0.021, 6, 16, Math.PI * 0.80]} />
        </mesh>
        <mesh castShadow rotation={[Math.PI / 2, 0, -0.42]} material={iron}>
          <torusGeometry args={[0.138, 0.024, 6, 14, Math.PI * 0.70]} />
        </mesh>
        <mesh castShadow rotation={[Math.PI / 2, 0, -0.10]} material={iron}>
          <torusGeometry args={[0.176, 0.014, 6, 16, Math.PI * 0.52]} />
        </mesh>
      </group>

      {/* ── THE BACK-SPIKE ── what makes it a bill */}
      <mesh castShadow position={[-0.09, 0.86, 0]} rotation={[0, 0, 1.15]} scale={[1, 1, 0.4]} material={iron}>
        <coneGeometry args={[0.032, 0.20, 6]} />
      </mesh>

      {/* the dim violet rule along the inside of the hook — not
          fire. The thing has never been on fire. */}
      <group position={[0, 0.80, 0]} scale={[1, 1, 0.06]}>
        <mesh rotation={[Math.PI / 2, 0, -0.28]}>
          <torusGeometry args={[0.156, 0.012, 5, 16, Math.PI * 0.86]} />
          <meshBasicMaterial color={hot} transparent opacity={0.34} toneMapped={false} depthWrite={false} />
        </mesh>
      </group>
      <HotEdge args={[0.176, 0.006, 4, 14, Math.PI * 0.5]}
        position={[0, 0.80, 0]} rotation={[Math.PI / 2, 0, -0.10]}
        color={hot} opacity={0.22} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE SWITCH — one case per weapon, and that is the whole API
// ════════════════════════════════════════════════════════════
// A LIST, not a category. `arms` on an enemy is a list of these
// strings indexed per body, so two things out of the same water
// are never carrying the same thing, and a new weapon is a
// function plus a line.
export default function FoeWeapon({ kind = "none", hot = "#ff8a3c" }) {
  if (kind === "scythe") return <Scythe hot={hot} />;
  if (kind === "pale") return <Pale hot={hot} />;
  if (kind === "kunai") return <Kunai hot={hot} />;
  if (kind === "bill") return <Bill hot={hot} />;
  return null;
}

// Which weapon a given body of a given foe is carrying. Two husks
// out of one pyre never grabbed the same thing.
export function weaponOf(foe, index = 0) {
  const arms = foe?.arms;
  if (!arms || !arms.length) return "none";
  return arms[index % arms.length] || "none";
}
